import type { SessionRecord } from '../types';

// All session data — records, transcripts, and the raw audio Blobs — lives in
// IndexedDB on the student's machine. Nothing is uploaded to any server we run.
// The `sessions` store doubles as the v1.2 progress-trend source (a clean seam).

const DB = 'ielts-harness';
const STORE = 'sessions';
const VERSION = 1;

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'session_id' });
        os.createIndex('started_at', 'started_at');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveSession(rec: SessionRecord): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(rec);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function getSession(id: string): Promise<SessionRecord | undefined> {
  const db = await open();
  const rec = await new Promise<SessionRecord | undefined>((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(id);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return rec;
}

export async function listSessions(): Promise<SessionRecord[]> {
  const db = await open();
  const all = await new Promise<SessionRecord[]>((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return all.sort((a, b) => (a.started_at < b.started_at ? 1 : -1));
}

export async function deleteSession(id: string): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
